import { CheckCircleFilled, CloseCircleFilled, InfoCircleFilled, LoadingOutlined, WarningFilled } from '@ant-design/icons';
import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import type { TransitionStatus } from 'react-transition-group';

import { Button } from '@/modules/ui/components/Button';
import type { Theme } from '@/styles/theme';

import { Level } from '../../constants';
import { hideMessage } from '../../services/actions';
import type { Message } from '../../types';
import * as felaRules from './MessageContent.rules';

interface MessageIconProps {
    theme: Theme;
}

const IconEnum = {
    [Level.SUCCESS]: ({ theme }: MessageIconProps) => <CheckCircleFilled style={{ color: theme.colors.messageIcons.success }} />,
    [Level.ERROR]: ({ theme }: MessageIconProps) => <CloseCircleFilled style={{ color: theme.colors.messageIcons.error }} />,
    [Level.INFO]: ({ theme }: MessageIconProps) => <InfoCircleFilled style={{ color: theme.colors.messageIcons.info }} />,
    [Level.WARNING]: ({ theme }: MessageIconProps) => <WarningFilled style={{ color: theme.colors.messageIcons.warning }} />,
    [Level.LOADING]: ({ theme }: MessageIconProps) => <LoadingOutlined style={{ color: theme.colors.messageIcons.loading }} />,
};

export interface MessageContentProps extends Omit<Message, 'visible'> {
    state: TransitionStatus;
}

export const MessageContent = ({ level, message, state, button, id }: MessageContentProps) => {
    const { css, theme } = useFela<Theme>({ state });
    const dispatch = useDispatch();
    const Icon = IconEnum[level];

    const handleOnClick = () => {
        if (typeof button?.onClick === 'function') {
            button.onClick();
        } else if (button?.onClick) {
            dispatch(button.onClick);
        }
        dispatch(hideMessage(id));
    };

    return (
        <div className={css(felaRules.content)} data-testid={`message-${typeof message === 'object' ? message.id : message}`}>
            <div className={css(felaRules.inner)}>
                <Icon theme={theme} />
                <div className={css(felaRules.text)}>{typeof message === 'object' ? <FormattedMessage {...message} /> : message}</div>
                {button ? (
                    <Button customStyle={felaRules.button} type='primary' onClick={handleOnClick}>
                        {typeof button.message === 'object' ? <FormattedMessage {...button.message} /> : button.message}
                    </Button>
                ) : null}
            </div>
        </div>
    );
};
